<template>
  <div class="flex grow pr-2 items-center">
    <Skeleton
      v-if="isLoadingTaskOrContent && !currentTaskKey"
      class="rounded-md grow-0 max-w-md"
      height="40px"
    />
    <div
      v-if="currentTask"
      :data-flip-key="currentTaskKey"
      class="p-2 max-w-md bg-surface-100 dark:bg-surface-700 rounded-md grid grid-cols-[auto_1fr_auto] gap-2 items-center w-full"
      @click="openMenu"
    >
      <span class="bg-primary rounded-md block w-max h-max p-1">
        <img
          :src="getTaskIcon(currentTask.key, currentTask.icon)"
          class="h-4 w-4 svg-color"
          @error="onImageLoadFailed"
        />
      </span>
      <div class="overflow-hidden">
        <p class="text-sm font-medium">{{ currentTask.label }}</p>
        <p class="text-xs text-surface-500 dark:text-surface-400 truncate">
          {{ currentTask.description }}
        </p>
      </div>

      <i
        v-if="
          currentTaskKey &&
          !isLoadingTaskOrContent &&
          !newTaskSTatus.isQueued &&
          !newTaskSTatus.isRunning &&
          !taskResult
        "
        class="pi pi-chevron-down text-sm text-surface-500 dark:text-surface-300"
      />
    </div>
    <MAInput
      v-if="!currentTaskKey && !isLoadingTaskOrContent"
      v-model="filter"
      class="grow max-w-sm [&>div>div>input]:shadow-transparent [&>div.relative]:pb-0 [&>.ma-label]:sr-only pt-0!"
      :placeholder="$t('contentFilterPlaceholder')"
      autofocus
    />

    <div v-if="currentTaskKey && taskResult" class="ml-auto">
      <AppButton
        v-tooltip.top="'Reset and create new content'"
        variant="secondary"
        icon="pi pi-undo"
        class="h-10 w-10"
        aria-controls="quick_task_picker"
        @click="confirmReset"
      />
    </div>

    <Menu id="quick_task_picker" ref="menu" :popup="true" :model="tasks">
      <template #submenuheader="{ item }">
        <span class="text-primary font-bold flex items-center gap-2 mt-2">
          <span class="block h-max w-max p-1 bg-primary rounded-md">
            <img :src="item.icon" class="h-4 w-4 svg-color" @error="onImageLoadFailed" />
          </span>
          {{ item.label }}
        </span>
      </template>

      <template #item="{ item, props: itemProps }">
        <button
          type="button"
          class="p-1 sm:py-2 px-2 sm:px-3 text-left overflow-hidden w-full"
          v-bind="itemProps"
          @click.stop="onTaskSelected(item)"
        >
          <div class="flex items-center gap-1">
            <span
              class="bg-surface-500/20 dark:bg-surface-600 rounded-md block w-max h-max p-1"
            >
              <img
                :src="item.icon"
                class="h-3 w-3 opacity-70 dark:invert"
                @error="onImageLoadFailed"
              />
            </span>
            <span class="text-sm font-medium">{{ item.label }}</span>
          </div>
          <span v-if="item.description" class="ma-text-secondary text-xs truncate block">
            {{ item.description }}
          </span>
        </button>
      </template>
    </Menu>
  </div>
</template>

<script setup lang="ts">
import Skeleton from "@/volt/Skeleton.vue";
import { useConfirm } from "primevue/useconfirm";
import Menu from "@/volt/Menu.vue";
import { useI18n } from "vue3-i18n";

const { isLoadingTaskOrContent, currentTaskKey, reset, newTaskSTatus, taskResult } =
  useQuickTask();
const { getTaskByName, getTaskIcon } = useTaskConfigDataCore();
const { fallbackIcon, tasks } = useTaskTypeOptions();
const filter = defineModel<string>();
const confirm = useConfirm();
const menu = ref<InstanceType<typeof Menu>>();
const { t } = useI18n();

const currentTask = computed(() => {
  if (!currentTaskKey.value) return null;
  return getTaskByName(currentTaskKey.value);
});

function openMenu(event: Event) {
  if (
    isLoadingTaskOrContent.value ||
    taskResult.value ||
    newTaskSTatus.value.isRunning ||
    newTaskSTatus.value.isQueued ||
    newTaskSTatus.value.isComplete
  )
    return;
  if (menu.value) {
    menu.value.toggle(event);
  }
}

const confirmReset = (event: Event) => {
  if (!event.currentTarget) return;
  confirm.require({
    target: event.currentTarget as HTMLElement,
    message: t("confirmResetContentGeneration"),
    icon: "pi pi-exclamation-triangle",
    rejectClass: "p-button-secondary p-button-outlined p-button-sm",
    acceptClass: "p-button-sm",
    rejectLabel: t("buttons.cancel"),
    acceptLabel: t("reset"),
    accept: reset,
  });
};

function onImageLoadFailed(e: Event) {
  if (!e.target) return;
  (e.target as HTMLImageElement).src = fallbackIcon;
}

function onTaskSelected(task: any) {
  const fullKey = `${task.namespace}/${task.key}`;
  currentTaskKey.value = fullKey;
  filter.value = "";
  menu.value?.hide();
}
</script>
